import { useEffect, useState } from 'react'

import { usePrevious } from '@wiggot/hooks/src/usePrevious'

import {
  ECriteriaSort,
  EPropertyOrder,
  EPropertyType,
  ESearchLevel,
} from '../../../types-library/src'

import { getPropertiesByParameters } from '../../../apis-library/src'

import { useFilterStore, usePropertiesStore } from '../../../state-library/src'
import {
  getMinDiscountValue,
  getMinPriceM2,
  getQuickLinkState,
  returnPriceToValidated,
} from '../utils'

const animateTime = 2000

const PropertiesLoader = ({
  cb,
}: {
  cb?: (params: any) => void
}): JSX.Element => {
  const {
    firtsTime,
    page,
    properties,
    propertyLoaderStatus,
    setPropertyLoaderStatus,
    setFirtsTime,
    setProperties,
    setPage,
  } = usePropertiesStore()

  const {
    rooms,
    bathrooms,
    parkings,
    constructionType,
    presale,
    propertyType,
    priceFrom,
    priceTo,
    latitude,
    longitude,
    searchLevel,
    searchAddress,
    opportunityCriteria,
    amenities,
    publishedDate,
    areaBounds,
    priceDiffFrom,
    priceDiffTo,
    capRateFrom,
    capRateTo,
    priceBySqmFrom,
    priceBySqmTo,
    pamRequestId,
    constructionYearFrom,
    constructionYearTo,
    propertyOrder,
    origin,
    showOnlyCommissionProperties,
    agencyId,
    associationIds,
    commissionRateLowRange,
    commissionRateHighRange,
    surfaceFrom,
    surfaceTo,
    listingsSource,
    wiggotUserType,
    locations,
  } = useFilterStore((state) => state)

  const currentPagePrevious = usePrevious<number>(page?.currentPage)

  /**
   * This function is used to load properties
   * @param pageNumber current page number
   * @returns {Promise<void>}
   */
  const loadProperties = async (params: {
    pageNumber: number
  }): Promise<void> => {
    try {
      const { pageNumber } = params

      setPropertyLoaderStatus({
        loading: true,
        error: false,
        waitingAnimation: true,
      })

      const queryParams = createRequestParams({
        pageNumber,
      })
      const { isSuccess, data } = await getPropertiesByParameters({
        ...queryParams,
      })
      if (cb) {
        cb(queryParams)
      }

      /** loader delay */
      await delayProcess({
        time: animateTime,
      })

      if (!isSuccess || !data?.items?.length) {
        throwPropertiesNotFound()
        return
      }

      setProperties(data?.items)

      setPage({
        currentPage: data?.currentPage,
        perpage: data?.perpage,
        totalItems: data?.totalItems,
        totalPages: data?.totalPages,
        globalCount: data?.globalCount,
      })

      setPropertyLoaderStatus({
        loading: false,
        error: false,
        waitingAnimation: true,
      })

      /** skeleton delay */
      await delayProcess({
        time: animateTime,
      })

      setPropertyLoaderStatus({
        loading: false,
        error: false,
        waitingAnimation: false,
      })
    } catch (error) {
      throwPropertiesNotFound()
    }
  }

  const delayProcess = (params: { time: number }): Promise<void> => {
    const { time } = params
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve()
      }, time)
    })
  }

  /**
   * This function is used to throw properties not found
   * @returns {void}
   */
  const throwPropertiesNotFound = (): void => {
    setProperties([])

    setPage({
      currentPage: 0,
      perpage: 0,
      totalItems: 0,
      totalPages: 0,
      globalCount: 0,
    })

    setPropertyLoaderStatus({
      loading: false,
      error: true,
      waitingAnimation: true,
    })

    setTimeout(() => {
      setPropertyLoaderStatus({
        loading: false,
        error: true,
        waitingAnimation: false,
      })
    }, animateTime)
  }
  /**
   * This function is used to create request params
   * @param pageNumber current page number
   * @returns {any}
   */
  const createRequestParams = (params: { pageNumber: number }): any => {
    const { pageNumber } = params

    // Al dibujar una zona en el mapa (areaBounds con datos), /properties/wiggot
    // siempre debe recibir searchLevel=state, sin importar el nivel de zoom
    // que haya calculado el drawing tool (ese searchLevel de zoom es el que
    // usan filter-summary/insights, y no debe tocarse aqui).
    let level: ESearchLevel
    if (areaBounds.length > 0) {
      level = ESearchLevel.STATE
    } else {
      switch (searchLevel) {
        case ESearchLevel.BlOCK:
        case ESearchLevel.PROPERTY:
          level = ESearchLevel.NEIGHBORHOOD
          break
        default:
          level = searchLevel
          break
      }
    }

    const quickLinkState = getQuickLinkState(searchAddress, {
      latitude: latitude,
      longitude: longitude,
    })

    // arrayCoordinates siempre debe ser el mismo formato de string plano
    // "[lng,lat],[lng,lat],..." que boundsFilter, nunca el JSON de objetos
    // {latitude,longitude}. Prioriza el area dibujada (areaBounds) sobre la
    // seleccion de varios vecindarios (locations), ya que son mutuamente
    // excluyentes en la UI.
    const arrayCoordinates =
      areaBounds.length > 0
        ? areaBounds.map((bound) => `[${bound.join(',')}]`).join(',')
        : locations.length > 0
          ? locations
              .map(({ latitude, longitude }) => `[${longitude},${latitude}]`)
              .join(',')
          : undefined

    const queryParams = {
      bathrooms,
      bedrooms: rooms,
      buildType: constructionType ? constructionType : undefined,
      presale: presale ? presale : undefined,
      parkings,
      searchLevel: level,
      amenities: amenities.length ? amenities.join(',') : undefined,
      allowEmptyCoordinates: true,
      type:
        propertyType === EPropertyType.SALE
          ? EPropertyType.SALE
          : EPropertyType.RENT,
      latitude,
      longitude,
      proxyPublishedDays: publishedDate,
      // boundsFilter ya no se manda a /properties/wiggot: esta API nueva no
      // lo usa (convertParamsToNewAPI lo descarta) y arrayCoordinates ahora
      // cubre exactamente el mismo caso (dibujo) con el formato correcto.
      budgetM2HighRange: priceBySqmTo ?? undefined,
      budgetM2LowRange: getMinPriceM2(priceBySqmFrom ?? 0, priceBySqmTo ?? 0),
      caprateHighRange:
        capRateTo === 1 && capRateFrom === 1 ? undefined : capRateTo,
      caprateLowRange:
        capRateTo === 1 && capRateFrom === 1 ? undefined : capRateFrom,
      discountHighRange: priceDiffTo ? priceDiffTo : undefined,
      discountLowRange: getMinDiscountValue(
        { min: priceDiffFrom, max: priceDiffTo } ?? { min: 0, max: 0 }
      ),
      quickLinkState,
      pamRequestId,
      builtYearFrom: constructionYearFrom ?? undefined,
      builtYearTo: constructionYearTo ?? undefined,
      origin,
      showOnlyCommissionProperties,
      listingsSource,
      wiggotUserType,
      associationIds,
      commissionRateLowRange,
      commissionRateHighRange,
      surfaceFrom: surfaceTo && !surfaceFrom ? 0 : surfaceFrom,
      surfaceTo: surfaceFrom && !surfaceTo ? 1000000 : surfaceTo,
      searchAddress,
      arrayCoordinates,
    }

    const sortCriteria = getSortCriteria({
      order: propertyOrder,
    })

    return {
      ...queryParams,
      budgetLowRange: priceFrom,
      budgetHighRange: returnPriceToValidated(priceTo, priceFrom, propertyType),
      page: pageNumber ?? 1,
      sortCriteria,
      opportunityCriteria,
    }
  }

  /**
   * This function is used to get sort criteria
   * @returns {ECriteriaSort}
   */
  const getSortCriteria = (params: {
    order: EPropertyOrder
  }): ECriteriaSort => {
    const { order } = params
    switch (order) {
      case EPropertyOrder.MAX_TO_MIN_PRICE:
        return ECriteriaSort.EXPENSIVE
      case EPropertyOrder.MIN_TO_MAX_PRICE:
        return ECriteriaSort.CHEAP
      case EPropertyOrder.MAX_TO_MIN_SIZE:
        return ECriteriaSort.LARGE
      case EPropertyOrder.MIN_TO_MAX_SIZE:
        return ECriteriaSort.SMALL
      case EPropertyOrder.RECENT_PUBLISHED:
        return ECriteriaSort.CREATED
      case EPropertyOrder.OLDEST:
        return ECriteriaSort.OLDEST
      default:
        return ECriteriaSort.RANKING
    }
  }

  /**
   * load properties when module is mounted
   */
  useEffect(() => {
    if (pamRequestId && !firtsTime) {
      loadProperties({
        pageNumber: 1,
      })
    } else if (latitude !== 0 && longitude !== 0 && !firtsTime) {
      loadProperties({
        pageNumber: 1,
      })
    }
  }, [
    latitude,
    longitude,
    bathrooms,
    constructionType,
    presale,
    opportunityCriteria,
    parkings,
    priceFrom,
    priceTo,
    propertyType,
    rooms,
    searchLevel,
    priceDiffFrom,
    priceDiffTo,
    constructionYearFrom,
    constructionYearTo,
    capRateFrom,
    capRateTo,
    priceBySqmFrom,
    priceBySqmTo,
    amenities,
    publishedDate,
    pamRequestId,
    areaBounds,
    showOnlyCommissionProperties,
    agencyId,
    associationIds,
    commissionRateLowRange,
    commissionRateHighRange,
    surfaceFrom,
    surfaceTo,
    listingsSource,
    locations,
  ])

  /**
   * load properties when order change
   */
  useEffect(() => {
    if (
      properties.length &&
      propertyOrder &&
      propertyOrder !== EPropertyOrder.RELEVANCE &&
      !propertyLoaderStatus.waitingAnimation
    ) {
      loadProperties({
        pageNumber: page.currentPage,
      })
    }
  }, [propertyOrder])

  /**
   * load properties when page change
   */
  useEffect(() => {
    if (
      !propertyLoaderStatus.waitingAnimation &&
      (page.currentPage > 1 ||
        (page.currentPage === 1 && currentPagePrevious === 2))
    ) {
      loadProperties({
        pageNumber: page.currentPage,
      })
    }
  }, [page?.currentPage])

  useEffect(() => {
    setTimeout(() => {
      setFirtsTime(false)
    }, 100)
  }, [])
  return <></>
}

export default PropertiesLoader
